import apiV2 from '../api/v2';

// products.vendor and products.vendor_order_method. Neither is ever deleted
// (products and purchase orders reference them); retirement is isActive.

export const MAX_VENDOR_RECIPIENTS = 20;
export const MAX_VENDOR_NAME_LENGTH = 120;
export const MAX_VENDOR_ID_LENGTH = 64;
// Mirrors VENDOR_ID_PATTERN in the backend's vendors.validator.ts.
export const VENDOR_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export type OrderMethodKind = 'email' | 'external';

export interface VendorReferences {
    linkedProducts: number;
    livePurchaseOrders: number;
}

export interface OrderMethodDetail {
    id: string;
    vendorId: string;
    name: string;
    kind: OrderMethodKind;
    // Only ever populated for kind 'email'; a DB CHECK forbids it otherwise.
    poRecipients: string[];
    isActive: boolean;
    // Decided by the send path's own classifier - authoritative over any
    // client-side derivation. See vendorDispatch.ts.
    isUsable: boolean;
    createdAt: string;
    updatedAt: string;
    references: VendorReferences;
}

// Rollup on the list, so the page ships no inbox addresses.
export interface OrderMethodSummary {
    total: number;
    email: number;
    external: number;
    active: number;
    usable: number;
}

export interface VendorListItem {
    id: string;
    name: string;
    isActive: boolean;
    orderMethods: OrderMethodSummary;
    createdAt: string;
    updatedAt: string;
    references: VendorReferences;
}

export interface VendorDetail {
    id: string;
    name: string;
    isActive: boolean;
    orderMethods: OrderMethodDetail[];
    createdAt: string;
    updatedAt: string;
    references: VendorReferences;
}

export interface VendorDispatchWarning {
    code: 'vendor_not_dispatchable';
    message: string;
    linkedProducts: number;
    livePurchaseOrders: number;
}

export interface VendorWriteResult {
    vendor: VendorDetail;
    warning: VendorDispatchWarning | null;
}

export interface CreateVendorBody {
    id: string;
    name: string;
    isActive: boolean;
}

// A vendor can only be renamed - retirement lives on its ordering methods.
export interface UpdateVendorBody {
    name?: string;
}

// `kind` is absent from the update body on purpose: a channel change is a new
// method, since products and past POs were assigned on the old basis.
export interface CreateOrderMethodBody {
    id: string;
    name: string;
    kind: OrderMethodKind;
    poRecipients?: string[];
    isActive?: boolean;
}

export interface UpdateOrderMethodBody {
    name?: string;
    poRecipients?: string[];
    isActive?: boolean;
}

const BASE_PATH = '/billing/vendors';

const vendorPath = (id: string) => `${BASE_PATH}/${encodeURIComponent(id)}`;
const methodsPath = (vendorId: string) => `${vendorPath(vendorId)}/methods`;

export const fetchVendor = async (id: string): Promise<VendorDetail> => {
    const response = await apiV2.get<{ success: boolean; data: VendorDetail }>(vendorPath(id));
    return response.data.data;
};

// detail=true returns the rollup shape; includeInactive is what makes retired
// vendors visible, and so un-retiring possible.
export const fetchVendorList = async (): Promise<VendorListItem[]> => {
    const response = await apiV2.get<{ success: boolean; data: VendorListItem[] }>(BASE_PATH, {
        params: { detail: 'true', includeInactive: 'true' }
    });
    return response.data.data;
};

export const createVendor = async (body: CreateVendorBody): Promise<VendorWriteResult> => {
    const response = await apiV2.post<{ success: boolean; data: VendorWriteResult }>(
        BASE_PATH,
        body
    );
    return response.data.data;
};

export const updateVendor = async (
    id: string,
    body: UpdateVendorBody
): Promise<VendorWriteResult> => {
    const response = await apiV2.patch<{ success: boolean; data: VendorWriteResult }>(
        vendorPath(id),
        body
    );
    return response.data.data;
};

export const createOrderMethod = async (
    vendorId: string,
    body: CreateOrderMethodBody
): Promise<OrderMethodDetail> => {
    const response = await apiV2.post<{ success: boolean; data: OrderMethodDetail }>(
        methodsPath(vendorId),
        body
    );
    return response.data.data;
};

// 204 when nothing references it; 409 names the blocking counts. EVERY purchase
// order blocks, not just live ones - surface the server's message, never guess.
export const deleteOrderMethod = async (vendorId: string, methodId: string): Promise<void> => {
    await apiV2.delete(`${methodsPath(vendorId)}/${encodeURIComponent(methodId)}`);
};

export const updateOrderMethod = async (
    vendorId: string,
    methodId: string,
    body: UpdateOrderMethodBody
): Promise<OrderMethodDetail> => {
    const response = await apiV2.patch<{ success: boolean; data: OrderMethodDetail }>(
        `${methodsPath(vendorId)}/${encodeURIComponent(methodId)}`,
        body
    );
    return response.data.data;
};
